<template>
  <article
    class="rc"
    :class="['rc--' + type, { 'rc--closed': closed, 'rc--wide': wide }]"
    :style="{ '--ac': accent }"
    tabindex="0"
    @click="$emit('open', item)"
    @keydown.enter="$emit('open', item)"
  >
    <div class="rc__top">
      <div class="rc__title">{{ title }}</div>
      <v-menu offset-y left>
        <template #activator="{ on }">
          <button type="button" class="rc__more" :aria-label="$t('crmMoveTo')" @click.stop v-on="on"><v-icon small>mdi-dots-vertical</v-icon></button>
        </template>
        <v-list dense>
          <v-subheader v-if="moveTargets.length">{{ $t('crmMoveTo') }}</v-subheader>
          <v-list-item v-for="t in moveTargets" :key="t.key" @click="$emit('move', item, t.target)">
            <span class="rc__dot" :style="{ background: t.color }" />{{ t.label }}
          </v-list-item>
          <v-divider v-if="moveTargets.length" />
          <v-list-item @click="$emit('log', item)"><v-icon small class="me-2">mdi-plus-circle-outline</v-icon>{{ $t('crmLogActivity') }}</v-list-item>
          <v-list-item v-if="type === 'lead' && item.status !== 'converted'" @click="$emit('convert', item)"><v-icon small class="me-2">mdi-swap-horizontal</v-icon>{{ $t('crmConvert') }}</v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div v-if="subtitle" class="rc__sub"><v-icon x-small>{{ type === 'lead' ? 'mdi-domain' : 'mdi-account-outline' }}</v-icon>{{ subtitle }}</div>

    <div v-if="type === 'deal'" class="rc__amount">
      <template v-if="item.financial_hidden || item.amount === null || item.amount === undefined">
        <v-icon x-small>mdi-lock-outline</v-icon><span class="rc__muted">—</span>
      </template>
      <template v-else>
        {{ money(item.amount, true) }}
        <span v-if="item.probability" class="rc__prob">{{ item.probability }}٪</span>
      </template>
    </div>
    <div v-else-if="wide || item.need" class="rc__need">{{ item.need }}</div>

    <div v-if="type === 'lead' && (item.score || item.readiness)" class="rc__bars">
      <div class="rc__bar" :title="$t('crmScore')"><i :style="{ width: (item.score || 0) + '%', background: scoreColor }" /></div>
      <span>{{ item.score || 0 }}</span>
    </div>

    <div class="rc__badges">
      <crm-chip v-if="showStatus" :value="item.status" small />
      <crm-chip v-if="item.priority && item.priority !== 'normal'" kind="priority" :value="item.priority" small />
      <span v-if="follow === 'overdue'" class="rc__flag rc__flag--red"><v-icon x-small>mdi-alarm</v-icon>{{ $t('crmOverdue') }}</span>
      <button
        v-else-if="follow === 'none' && !closed"
        type="button"
        class="rc__flag rc__flag--amber rc__flag--btn"
        :title="$t('crmScheduleFollowUp')"
        @click.stop="$emit('log', item, 'call', 'schedule')"
      >
        <v-icon x-small>mdi-calendar-remove-outline</v-icon>{{ $t('crmNoFollowUp') }}
        <v-icon x-small class="rc__flag-plus">mdi-plus</v-icon>
      </button>
      <span v-if="item.is_stale" class="rc__flag rc__flag--grey"><v-icon x-small>mdi-sleep</v-icon>{{ $t('crmStale') }}</span>
      <span v-for="t in tags" :key="t" class="rc__tag">#{{ t }}</span>
    </div>

    <button
      v-if="item.last_activity_at || item.activities_count"
      type="button"
      class="rc__last"
      :title="$t('crmShowActivities')"
      @click.stop="$emit('open', item, 'activity')"
    >
      <v-icon x-small>mdi-history</v-icon>
      <span>{{ $t('crmLastActivity') }}: {{ ago(item.last_activity_at) }}</span>
      <span v-if="item.activities_count" class="rc__last-n">{{ item.activities_count }}</span>
    </button>

    <div class="rc__foot">
      <crm-avatar :user-id="item.owner_user_id" :size="22" />
      <span v-if="follow !== 'none'" class="rc__date" :class="'rc__date--' + follow">
        <v-icon x-small>mdi-calendar-clock-outline</v-icon>{{ fmtDate(item.next_follow_up_at, true) }}
      </span>
      <span v-else-if="type === 'deal' && item.expected_close_date" class="rc__date">
        <v-icon x-small>mdi-flag-checkered</v-icon>{{ fmtDate(item.expected_close_date) }}
      </span>
      <v-spacer />
      <div v-if="!closed" class="rc__quick" @click.stop @keydown.stop>
        <button type="button" class="rc__qa" :title="$t('crmQuickLogCall')" @click="$emit('log', item, 'call', 'done')"><v-icon x-small>mdi-phone-check-outline</v-icon></button>
        <button type="button" class="rc__qa" :title="$t('crmQuickNote')" @click="$emit('log', item, 'note', 'done')"><v-icon x-small>mdi-note-edit-outline</v-icon></button>
        <button type="button" class="rc__qa" :title="$t('crmScheduleFollowUp')" @click="$emit('log', item, 'call', 'schedule')"><v-icon x-small>mdi-calendar-plus</v-icon></button>
      </div>
      <a v-if="phone" :href="'tel:' + phone" class="rc__call" :title="$t('crmCall') + ' ' + phone" @click.stop="$emit('log', item, 'call', 'done')"><v-icon x-small>mdi-phone</v-icon></a>
    </div>
  </article>
</template>

<script>
import { money, fmtDate, relDays, followState, fullName, dealCustomer, STATUS_COLORS } from '../crmStore'
import { CrmChip, CrmAvatar } from './CrmBits'

export default {
  name: 'CrmRecordCard',
  components: { CrmChip, CrmAvatar },
  props: {
    item: { type: Object, required: true },
    type: { type: String, default: 'deal' },
    color: { type: String, default: '' },
    moveTargets: { type: Array, default: () => [] },
    showStatus: Boolean,
    wide: Boolean
  },
  computed: {
    title () { return this.type === 'lead' ? (fullName(this.item) || this.item.company_name || '#' + this.item.id) : this.item.title },
    subtitle () { return this.type === 'lead' ? (fullName(this.item) ? this.item.company_name : '') : dealCustomer(this.item) },
    accent () { return this.color || STATUS_COLORS[this.item.status] || '#2166d1' },
    closed () { return ['won', 'lost', 'converted', 'rejected', 'archived'].includes(this.item.status) },
    follow () { return followState(this.item.next_follow_up_at) },
    tags () { return Array.isArray(this.item.tags) ? this.item.tags.slice(0, 2) : [] },
    phone () {
      const r = this.item
      return r.mobile || r.phone || (r.person && r.person.mobile) || ''
    },
    scoreColor () {
      const s = this.item.score || 0
      return s >= 70 ? '#0f9d76' : s >= 40 ? '#d9822b' : '#8a94a6'
    }
  },
  methods: {
    money,
    fmtDate,
    ago (v) {
      const d = relDays(v)
      if (d === null) return '—'
      if (d === 0) return this.$t('crmGroupToday')
      if (d === -1) return this.$t('crmYesterday')
      return this.$t('crmDaysAgo', { n: Math.abs(d) })
    }
  }
}
</script>

<style scoped>
.rc { position: relative; background: #fff; border: 1px solid rgba(15, 23, 42, .08); border-radius: 6px; padding: 10px 12px 10px; cursor: pointer; box-shadow: 0 1px 2px rgba(15, 23, 42, .04); transition: box-shadow .15s, transform .15s, border-color .15s; outline: none; }
.rc::before { content: ''; position: absolute; inset-inline-start: 0; top: 10px; bottom: 10px; width: 3px; border-radius: 0 3px 3px 0; background: var(--ac); }
.rc:hover, .rc:focus-visible { box-shadow: 0 4px 14px rgba(15, 23, 42, .09); border-color: rgba(15, 23, 42, .14); }
.rc:focus-visible { border-color: var(--v-primary-base); }
.theme--dark .rc { background: #262626; border-color: rgba(255, 255, 255, .08); }
.rc--closed { opacity: .72; }
.rc__top { display: flex; align-items: flex-start; gap: 6px; }
.rc__title { flex: 1; min-width: 0; font-weight: 700; font-size: .86rem; line-height: 1.45; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.rc__more { width: 24px; height: 24px; margin: -2px -4px 0 0; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; opacity: 0; transition: opacity .15s; flex-shrink: 0; }
.rc:hover .rc__more, .rc:focus-within .rc__more { opacity: 1; }
@media (hover: none) { .rc__more { opacity: 1; } }
.rc__more:hover { background: rgba(127, 127, 127, .12); }
.rc__sub { display: flex; align-items: center; gap: 4px; font-size: .76rem; opacity: .65; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rc__amount { margin-top: 8px; font-weight: 800; font-size: .92rem; display: flex; align-items: center; gap: 6px; }
.rc__prob { font-size: .68rem; font-weight: 600; padding: 1px 6px; border-radius: 6px; background: rgba(127, 127, 127, .12); }
.rc__muted { opacity: .5; }
.rc__need { margin-top: 6px; font-size: .78rem; opacity: .8; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.rc__bars { display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: .7rem; font-weight: 700; }
.rc__bar { flex: 1; height: 4px; border-radius: 4px; background: rgba(127, 127, 127, .15); overflow: hidden; }
.rc__bar i { display: block; height: 100%; border-radius: 4px; }
.rc__badges { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px; }
.rc__badges:empty { display: none; }
.rc__flag { display: inline-flex; align-items: center; gap: 3px; height: 20px; padding: 0 6px; border-radius: 6px; font-size: .68rem; font-weight: 600; }
.rc__flag--red { background: rgba(192, 57, 43, .1); color: #c0392b; }
.rc__flag--red ::v-deep .v-icon { color: #c0392b !important; }
.rc__flag--amber { background: rgba(217, 130, 43, .12); color: #b86a1a; }
.rc__flag--amber ::v-deep .v-icon { color: #b86a1a !important; }
.rc__flag--grey { background: rgba(127, 127, 127, .14); }
.rc__tag { font-size: .68rem; padding: 0 5px; height: 20px; line-height: 20px; border-radius: 6px; color: var(--v-primary-base); background: rgba(25, 118, 210, .07); }
.rc__foot { display: flex; align-items: center; gap: 6px; margin-top: 10px; }
.rc__date { display: inline-flex; align-items: center; gap: 3px; font-size: .7rem; opacity: .7; }
.rc__date--overdue { color: #c0392b; opacity: 1; font-weight: 600; }
.rc__date--today { color: var(--v-primary-base); opacity: 1; font-weight: 600; }
.rc__last { display: flex; align-items: center; gap: 4px; width: 100%; margin-top: 8px; padding: 4px 6px; border-radius: 6px; font-size: .7rem; opacity: .75; text-align: start; transition: background .15s; }
.rc__last:hover { background: rgba(25, 118, 210, .08); opacity: 1; color: var(--v-primary-base); }
.rc__last-n { margin-inline-start: auto; padding: 0 6px; border-radius: 6px; background: rgba(127, 127, 127, .15); font-weight: 700; }
.rc__flag--btn { cursor: pointer; transition: background .15s; }
.rc__flag--btn:hover { background: rgba(217, 130, 43, .22); }
.rc__flag-plus { margin-inline-start: 2px; }
.rc__quick { display: inline-flex; gap: 2px; opacity: 0; transform: translateY(2px); transition: opacity .15s, transform .15s; }
.rc:hover .rc__quick, .rc:focus-within .rc__quick { opacity: 1; transform: none; }
@media (hover: none) { .rc__quick { opacity: 1; transform: none; } }
.rc__qa { width: 26px; height: 26px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; background: rgba(127, 127, 127, .08); transition: background .15s; }
.rc__qa:hover { background: rgba(25, 118, 210, .14); }
.rc__qa:hover ::v-deep .v-icon { color: var(--v-primary-base) !important; }
.rc__call { width: 24px; height: 24px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; background: rgba(15, 157, 118, .12); text-decoration: none; }
.rc__call ::v-deep .v-icon { color: #0f9d76 !important; }
.rc__dot { width: 8px; height: 8px; border-radius: 50%; margin-inline-end: 8px; }
</style>
